import type { ReactNode } from 'react';
import { Ban, Check, Hourglass, TimerOff, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { OperatorStatus } from '@/lib/api';

/**
 * Status badge for a <c>PlatformAccount</c>. Combines colour + icon + text so
 * the status is identifiable in monochrome mode (design D12 / WCAG 2.1 AA).
 *
 * For <c>Pending</c> accounts with a known <c>pendingExpiresAt</c>, the
 * badge surfaces the proximity of the expiration:
 * <list type="bullet">
 *   <item><c>diff &gt; 6h</c>: warning "Pendente — expira em Nh"</item>
 *   <item><c>1h ≤ diff ≤ 6h</c>: warning with explicit warning copy</item>
 *   <item><c>0 &lt; diff &lt; 1h</c>: error "expira em Nmin"</item>
 *   <item><c>diff ≤ 0</c>: neutral "Pendente (expirado)"</item>
 * </list>
 * The full ISO timestamp goes into the <c>title</c> attribute so the admin
 * can verify the exact moment without manual math.
 */
export function OperatorStatusBadge({
  status,
  pendingExpiresAt,
}: {
  status: OperatorStatus;
  pendingExpiresAt?: string | null;
}) {
  if (status === 'Pending' && pendingExpiresAt) {
    const expiresMs = new Date(pendingExpiresAt).getTime();
    // Ler o relogio no render e o proprio ponto deste badge: ele existe para
    // dizer quanto falta para o convite expirar. Congelar a leitura em estado
    // mostraria "expira em 3h" indefinidamente numa aba aberta. A granularidade
    // e de horas/minutos, e a instabilidade que a regra alerta nao muda o que o
    // operador ve.
    // eslint-disable-next-line react-hooks/purity
    const diffMs = expiresMs - Date.now();
    const tooltip = `Expira em ${formatAbsolute(pendingExpiresAt)}`;

    if (diffMs <= 0) {
      // Expirado: neutro (não acionável até reenviar).
      return (
        <StatusBadge tone="neutral" icon={TimerOff} title={tooltip}>
          Pendente (expirado)
        </StatusBadge>
      );
    }

    if (diffMs < 3_600_000 /* < 1h */) {
      const minutes = Math.max(1, Math.ceil(diffMs / 60_000));
      return (
        <StatusBadge tone="error" icon={TriangleAlert} title={tooltip}>
          Pendente (expira em {minutes} min)
        </StatusBadge>
      );
    }

    if (diffMs <= 21_600_000 /* ≤ 6h */) {
      const hours = Math.max(1, Math.ceil(diffMs / 3_600_000));
      return (
        <StatusBadge tone="warning" icon={Hourglass} title={tooltip}>
          Pendente (expira em {hours}h)
        </StatusBadge>
      );
    }

    // Default Pending state: warning, hours-resolution copy.
    const hoursLeft = Math.max(1, Math.floor(diffMs / 3_600_000));
    return (
      <StatusBadge tone="warning" icon={Hourglass} title={tooltip}>
        Pendente — expira em {hoursLeft}h
      </StatusBadge>
    );
  }
  if (status === 'Active') {
    return (
      <StatusBadge tone="success" icon={Check}>
        Ativo
      </StatusBadge>
    );
  }
  if (status === 'Revoked') {
    return (
      <StatusBadge tone="neutral" icon={Ban}>
        Revogado
      </StatusBadge>
    );
  }
  // Fallback for Pending without expiry context.
  return (
    <StatusBadge tone="warning" icon={Hourglass}>
      Pendente
    </StatusBadge>
  );
}

// Cores de estado do livro (cap. 5): Mato no sucesso, Mostarda no aviso e Pimenta no erro, como
// borda do selo, sobre a tinta clara do estado, com o texto e o ícone no tom que lê sobre ela (Mato,
// Canela e Vinho). A borda conta como borda de componente contra a superfície de fora (Mostarda
// sobre Branco dá 3,19:1), e não contra a tinta clara (sobre Manteiga, 2,86:1). O neutro é Areia
// com Pedra e borda Linho. Ícone Lucide de traço, nunca emoji (regra 10; A14 e A15).
type Tone = 'success' | 'warning' | 'error' | 'neutral';

const TONES: Record<Tone, { className: string; background: string; color: string; border: string }> = {
  success: {
    className: 'badge badge-success',
    background: 'var(--color-success-light)',
    color: 'var(--color-success)',
    border: 'var(--color-success)',
  },
  warning: {
    className: 'badge badge-warning',
    background: 'var(--color-warning-light)',
    color: 'var(--color-warning-text)',
    border: 'var(--color-warning)',
  },
  error: {
    className: 'badge badge-error',
    background: 'var(--color-error-light)',
    color: 'var(--color-error-dark)',
    border: 'var(--color-error)',
  },
  neutral: {
    className: 'badge',
    background: 'var(--color-neutral-light)',
    color: 'var(--color-text-secondary)',
    border: 'var(--color-border)',
  },
};

function StatusBadge({
  tone,
  icon: Icon,
  title,
  children,
}: {
  tone: Tone;
  icon: LucideIcon;
  title?: string;
  children: ReactNode;
}) {
  const colors = TONES[tone];
  return (
    <span
      className={colors.className}
      data-tone={tone}
      style={{
        background: colors.background,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
      role="status"
      title={title}
    >
      {/* Chip de 12 px: exceção nomeada do livro (cap. 8, traço 2 em seta e chip de 12 px, selos). */}
      <Icon size={12} strokeWidth={2} aria-hidden="true" focusable="false" />
      {children}
    </span>
  );
}

function formatAbsolute(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}
